import { AlertTriangle, CheckCircle, DollarSign } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  EvidenceCard,
  EvidenceData,
} from '@/components/citations/EvidenceCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetMatterContextQuery } from '@/services/api/matterContextService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import {
  ProviderGroupViewModel,
  toProviderGroups,
} from '@/services/caseContextAdapter';
import {
  BenefitType,
  CoverageCategory,
  CoverageSchema,
} from '@/services/types/coverage-types';
import {
  MedicalProvider,
  ProviderMention,
} from '@/services/types/treatment-types';

import { MatterSectionCard } from './MatterSectionCard';

interface MedicalExpensesCardProps {
  matterId: string;
  hideHeader?: boolean;
}

interface ProviderRowProps {
  provider: string;
  totalBilled: string;
  hasSources: boolean;
  onViewSources: () => void;
}

const ProviderRow = ({
  provider,
  totalBilled,
  hasSources,
  onViewSources,
}: ProviderRowProps) => {
  return (
    <div className="flex justify-between items-center py-1 border-b border-zinc-50 last:border-0">
      {hasSources ? (
        <button
          type="button"
          onClick={onViewSources}
          className="text-xs text-zinc-700 truncate max-w-[60%] text-left border-b border-dashed border-zinc-400 hover:border-zinc-600 hover:text-zinc-900 transition-colors cursor-pointer"
          title="Click to view source documents"
        >
          {provider}
        </button>
      ) : (
        <span className="text-xs text-zinc-700 truncate max-w-[60%]">
          {provider}
        </span>
      )}
      <span className="text-xs font-medium text-zinc-900">{totalBilled}</span>
    </div>
  );
};

// Modal to show provider source citations
interface ProviderSourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerName: string;
  mentions: ProviderMention[];
  matterId: string;
}

const ProviderSourcesModal = ({
  isOpen,
  onClose,
  providerName,
  mentions,
  matterId,
}: ProviderSourcesModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">
            Sources for {providerName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {mentions.length === 0 ? (
            <p className="text-sm text-zinc-500">No source documents found.</p>
          ) : (
            mentions.map((mention) => {
              const evidenceData: EvidenceData = {
                artifact_id: mention.artifact_id,
                citation_ids: mention.citation_ids,
                rationale: mention.reasoning,
                excerpts: mention.excerpts,
                confidence: mention.confidence,
                artifact_type: mention.artifact_type,
              };
              return (
                <EvidenceCard
                  key={mention.id}
                  evidence={evidenceData}
                  matterId={matterId}
                  variant="minimal"
                  showSnippet
                />
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const formatDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const parseMoneyToNumber = (display?: string | null): number => {
  if (!display) return 0;
  const num = parseFloat(String(display).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(num) ? num : 0;
};

const formatMoney = (n: number): string => {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const formatMoneyWithCents = (n: number): string => {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface PolicyLimitInfo {
  policyLimit: number;
  benefitType: string;
  carrierName: string | null;
}

const getBenefitTypeLabel = (benefitType: BenefitType): string => {
  if (benefitType === BenefitType.MEDICAL_PAYMENTS) return 'MedPay';
  return 'PIP';
};

const getPolicyLimitFromCoverage = (
  coverage: CoverageSchema | undefined,
): PolicyLimitInfo | null => {
  if (!coverage?.benefits) return null;

  // Look for MedPay or PIP benefits which are relevant for medical expenses
  const relevantBenefitTypes = [
    BenefitType.MEDICAL_PAYMENTS,
    BenefitType.PERSONAL_INJURY_PROTECTION,
  ];

  // Find the first benefit with limitations
  const matchedBenefit = relevantBenefitTypes
    .map((type) => {
      const benefit = coverage.benefits?.find((b) => b.benefit_type === type);
      return benefit?.limitations ? { type, benefit } : null;
    })
    .find((result) => result !== null);

  if (!matchedBenefit) return null;

  const { type, benefit } = matchedBenefit;
  const { limitations } = benefit;

  if (!limitations) return null;

  // Get the "per person" limit, or the first available limit
  const perPersonLimit = limitations['per person'];
  const limit = perPersonLimit || Object.values(limitations)[0];

  if (!limit) return null;

  return {
    policyLimit: limit,
    benefitType: getBenefitTypeLabel(type),
    carrierName: coverage.carrier?.name || null,
  };
};

interface PolicyLimitProgressProps {
  totalExpenses: number;
  policyLimit: number;
  benefitType: string;
  carrierName: string | null;
}

const PolicyLimitProgress = ({
  totalExpenses,
  policyLimit,
  benefitType,
  carrierName,
}: PolicyLimitProgressProps) => {
  const percentage = Math.min((totalExpenses / policyLimit) * 100, 100);
  const isOverLimit = totalExpenses > policyLimit;
  const isNearLimit = percentage >= 80 && !isOverLimit;
  const remaining = policyLimit - totalExpenses;

  // Determine color based on usage
  let progressColor = 'bg-emerald-500';
  let textColor = 'text-emerald-600';
  let bgColor = 'bg-emerald-50';
  if (isOverLimit) {
    progressColor = 'bg-red-500';
    textColor = 'text-red-600';
    bgColor = 'bg-red-50';
  } else if (isNearLimit) {
    progressColor = 'bg-amber-500';
    textColor = 'text-amber-600';
    bgColor = 'bg-amber-50';
  }

  return (
    <div className={`mb-3 p-2.5 ${bgColor} rounded-lg`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-zinc-700">
            {benefitType}
          </span>
          {carrierName && (
            <span className="text-[10px] text-zinc-400">({carrierName})</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isOverLimit && <AlertTriangle className="h-3 w-3 text-red-500" />}
          {isNearLimit && <AlertTriangle className="h-3 w-3 text-amber-500" />}
          {!isOverLimit && !isNearLimit && (
            <CheckCircle className="h-3 w-3 text-emerald-500" />
          )}
          <span className={`text-xs font-bold ${textColor}`}>
            {percentage.toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Custom progress bar for color control */}
      <div className="h-2 w-full bg-white/60 rounded-full overflow-hidden">
        <div
          className={`h-full ${progressColor} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex justify-between items-center mt-1.5">
        <span className="text-[10px] text-zinc-500">
          {formatMoney(totalExpenses)} / {formatMoney(policyLimit)}
        </span>
        <span className={`text-[10px] font-medium ${textColor}`}>
          {isOverLimit
            ? `${formatMoney(Math.abs(remaining))} over`
            : `${formatMoney(remaining)} left`}
        </span>
      </div>
    </div>
  );
};

// Helper to normalize provider names for matching
const normalizeForMatch = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim();
};

// Find matching MedicalProvider by name
const findProviderMentions = (
  providerName: string,
  medicalProviders: MedicalProvider[] | undefined,
): ProviderMention[] => {
  if (!medicalProviders) return [];

  const normalizedName = normalizeForMatch(providerName);

  // Find provider with matching name
  const matchedProvider = medicalProviders.find((mp) => {
    const mpName = normalizeForMatch(mp.name || '');
    return (
      mpName === normalizedName ||
      mpName.includes(normalizedName) ||
      normalizedName.includes(mpName)
    );
  });

  return matchedProvider?.mentions || [];
};

export const MedicalExpensesCard = ({
  matterId,
  hideHeader,
}: MedicalExpensesCardProps) => {
  const [selectedProvider, setSelectedProvider] = useState<{
    name: string;
    mentions: ProviderMention[];
  } | null>(null);

  const { data: caseContext, isLoading: isLoadingContext } =
    useGetMatterContextQuery({
      matterId,
    });

  const { data: coverages, isLoading: isLoadingCoverages } =
    useGetCoveragesQuery({ matterId });

  const { data: medicalProviders, isLoading: isLoadingProviders } =
    useGetMedicalProvidersQuery(matterId);

  const isLoading =
    isLoadingContext || isLoadingCoverages || isLoadingProviders;

  const providerGroups: ProviderGroupViewModel[] = useMemo(() => {
    if (!caseContext) return [];
    return toProviderGroups(caseContext);
  }, [caseContext]);

  // Create a map of provider names to their mentions
  const providerMentionsMap = useMemo(() => {
    const map = new Map<string, ProviderMention[]>();
    providerGroups.forEach((group) => {
      const mentions = findProviderMentions(group.provider, medicalProviders);
      if (mentions.length > 0) {
        map.set(group.provider, mentions);
      }
    });
    return map;
  }, [providerGroups, medicalProviders]);

  const handleViewSources = (providerName: string) => {
    const mentions = providerMentionsMap.get(providerName) || [];
    setSelectedProvider({ name: providerName, mentions });
  };

  // Calculate total from provider subtotals if total_medical_bills is not available
  const calculatedTotal = useMemo(() => {
    return providerGroups.reduce((sum, group) => {
      return sum + parseMoneyToNumber(group.subtotalDisplay);
    }, 0);
  }, [providerGroups]);

  // Use caseContext total if available, otherwise use calculated total
  const totalFromContext = parseMoneyToNumber(caseContext?.total_medical_bills);
  const totalExpensesNumber =
    totalFromContext > 0 ? totalFromContext : calculatedTotal;
  const totalMedicalBillsDisplay = formatMoneyWithCents(totalExpensesNumber);

  const lastUpdated = formatDate(caseContext?.updated_at);

  // Get first-party coverage (client's own insurance) for policy limits
  const clientCoverage = useMemo(() => {
    return coverages?.find(
      (c) =>
        c.category === CoverageCategory.FIRST_PARTY ||
        c.category === CoverageCategory.FIRST_PARTY_HEALTH,
    );
  }, [coverages]);

  const policyLimitInfo = useMemo(() => {
    return getPolicyLimitFromCoverage(clientCoverage);
  }, [clientCoverage]);

  if (isLoading) {
    return (
      <MatterSectionCard
        title={hideHeader ? undefined : 'Medical Expenses'}
        icon={hideHeader ? undefined : <DollarSign className="h-4 w-4" />}
        isLoading
      >
        <div />
      </MatterSectionCard>
    );
  }

  const hasData = providerGroups.length > 0 || totalExpensesNumber > 0;

  if (!hasData) {
    return (
      <MatterSectionCard
        title={hideHeader ? undefined : 'Medical Expenses'}
        icon={hideHeader ? undefined : <DollarSign className="h-4 w-4" />}
      >
        <p className="text-sm font-medium text-zinc-500">
          No medical expense data on file
        </p>
      </MatterSectionCard>
    );
  }

  return (
    <MatterSectionCard
      title={hideHeader ? undefined : 'Medical Expenses'}
      icon={hideHeader ? undefined : <DollarSign className="h-4 w-4" />}
      bodyClassName="p-3"
    >
      {/* Policy Limit Progress Bar */}
      {policyLimitInfo && totalExpensesNumber > 0 && (
        <PolicyLimitProgress
          totalExpenses={totalExpensesNumber}
          policyLimit={policyLimitInfo.policyLimit}
          benefitType={policyLimitInfo.benefitType}
          carrierName={policyLimitInfo.carrierName}
        />
      )}

      {/* Provider Summary List - Scrollable */}
      {providerGroups.length > 0 && (
        <div className="max-h-40 overflow-y-auto mb-2 pr-1">
          {providerGroups.map((group) => (
            <ProviderRow
              key={group.provider}
              provider={group.provider}
              totalBilled={group.subtotalDisplay}
              hasSources={providerMentionsMap.has(group.provider)}
              onViewSources={() => handleViewSources(group.provider)}
            />
          ))}
        </div>
      )}

      {/* Total Section */}
      <div className="pt-2 border-t border-zinc-200">
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold text-zinc-700">Total</span>
          <span className="text-sm font-bold text-zinc-900">
            {totalMedicalBillsDisplay}
          </span>
        </div>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <p className="text-[10px] text-zinc-400 mt-1.5">
          Updated: {lastUpdated}
        </p>
      )}

      {/* Provider Sources Modal */}
      {selectedProvider && (
        <ProviderSourcesModal
          isOpen={!!selectedProvider}
          onClose={() => setSelectedProvider(null)}
          providerName={selectedProvider.name}
          mentions={selectedProvider.mentions}
          matterId={matterId}
        />
      )}
    </MatterSectionCard>
  );
};
